<script setup>
import { ref, reactive, defineEmits, onMounted, watch } from 'vue'
import { helpers, required, requiredIf } from '@vuelidate/validators'
import { useToast } from 'primevue/usetoast'

import errorHandler from '@/helpers/errorhandler'
import FxReportContainer from '@/components/FxReportContainer'
import LaporanApotekerService from '@/services/LaporanApotekerService'
import PabrikService from '@/services/PabrikService'
import useVuelidate from '@vuelidate/core'
import { saveAs } from 'file-saver'

const toast = useToast()
const laporanService = new LaporanApotekerService()
const pabrikService = new PabrikService()
let dialogViewer = ref(false)
let pdfSource = ref('')
let pdfTitle = ref('PDF Viewer')
let pdfFilename = ref('')

let isLoadingExport = ref(false)
let isLoadingPrint = ref(false)
let isLoading = ref(false)
let form = reactive({
  mode: 'triwulan',
  triwulan: null,
  bulan: null,
  pabrik: null,
  tahun: new Date().getFullYear(),
})
let submitted = ref(false)
let list_pabrik = ref([])
let list_triwulan = ref([])
let list_bulan = ref([])
let list_tahun = ref([])
let list_mode = [
  { name: 'Triwulan', value: 'triwulan' },
  { name: 'Bulanan', value: 'bulan' },
]

onMounted(async () => {
  list_triwulan.value = [
    { id: '1', nama: '01 Jan - 31 Mar' },
    { id: '2', nama: '01 Apr - 31 Jun' },
    { id: '3', nama: '01 Jul - 30 Sep' },
    { id: '4', nama: '01 Okt - 31 Des' },
  ]

  list_bulan.value = [
    { id: '1', nama: 'Januari' },
    { id: '2', nama: 'Februari' },
    { id: '3', nama: 'Maret' },
    { id: '4', nama: 'April' },
    { id: '5', nama: 'Mei' },
    { id: '6', nama: 'Juni' },
    { id: '7', nama: 'Juli' },
    { id: '8', nama: 'Agustus' },
    { id: '9', nama: 'September' },
    { id: '10', nama: 'Oktober' },
    { id: '11', nama: 'November' },
    { id: '12', nama: 'Desember' },
  ]

  const currentYear = new Date().getFullYear()
  list_tahun.value = Array.from(
    { length: 5 },
    (_, index) => currentYear - index
  )
})

watch(
  () => form.mode,
  () => {
    form.triwulan = null
    form.bulan = null
    submitted.value = false
  }
)

const rules = {
  mode: {
    required: helpers.withMessage('Jenis periode harus diisi.', required),
  },
  triwulan: {
    required: helpers.withMessage(
      'Periode triwulan harus diisi.',
      requiredIf(() => form.mode === 'triwulan')
    ),
  },
  bulan: {
    required: helpers.withMessage(
      'Bulan harus diisi.',
      requiredIf(() => form.mode === 'bulan')
    ),
  },
}

const v$ = useVuelidate(rules, form)

// eslint-disable-next-line no-unused-vars
const emit = defineEmits(['toggle'])

function searchPabrik(e) {
  pabrikService
    .get(`?filter[nama]=${e.query}`)
    .then((res) => {
      list_pabrik.value = res.data.data
    })
    .catch((err) => {
      errorHandler(err, 'Data Pabrik', toast)
    })
}

function getFilterParams() {
  let pabrikId = 0
  if (form.pabrik) pabrikId = form.pabrik.id

  return {
    mode: form.mode,
    triwulan: form.mode === 'triwulan' ? form.triwulan : null,
    bulan: form.mode === 'bulan' ? form.bulan : null,
    pabrikId,
    tahun: form.tahun,
  }
}

const triwulanRoman = { 1: 'i', 2: 'ii', 3: 'iii', 4: 'iv' }

function getReportFilename(ext) {
  if (form.mode === 'bulan') {
    const bulan = list_bulan.value.find((b) => b.id === form.bulan)
    const bulanSlug = (bulan?.nama || form.bulan || 'bulan')
      .toString()
      .toLowerCase()
    return `laporan-apoteker-triwulan-bulan-${bulanSlug}.${ext}`
  }

  const roman = triwulanRoman[form.triwulan] || form.triwulan
  return `laporan-apoteker-triwulan-${roman}.${ext}`
}

function onExport(isFormValid) {
  submitted.value = true
  if (!isFormValid) {
    return
  }
  isLoadingExport.value = true

  const { mode, triwulan, bulan, pabrikId, tahun } = getFilterParams()
  isLoading.value = true
  laporanService
    .exportLaporanTriwulanBarang(mode, triwulan, bulan, pabrikId, tahun)
    .then((res) => {
      isLoadingExport.value = false
      const url = window.URL.createObjectURL(new Blob([res.data]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', getReportFilename('xls'))
      document.body.appendChild(link)
      link.click()
    })

    .catch((err) => {
      errorHandler(err, 'Data Apoteker E-Report per Barang', toast)
    })
    .finally(() => ((isLoadingExport.value = false), (isLoading.value = false)))
}

function onPrint(isFormValid) {
  submitted.value = true
  if (!isFormValid) {
    return
  }
  isLoadingPrint.value = true

  const { mode, triwulan, bulan, pabrikId, tahun } = getFilterParams()
  isLoading.value = true
  laporanService
    .printLaporanTriwulanBarang(mode, triwulan, bulan, pabrikId, tahun)
    .then((res) => {
      const blob = new Blob([res.data], { type: 'application/pdf' })
      const pdfUrl = URL.createObjectURL(blob)
      pdfSource.value = pdfUrl + '#toolbar=0'
      pdfFilename.value = getReportFilename('pdf')
      pdfTitle.value = 'PDF Viewer (' + pdfFilename.value + ')'
      dialogViewer.value = true
    })

    .catch((err) => {
      errorHandler(err, 'Data Apoteker E-Report per Barang', toast)
    })
    .finally(() => ((isLoadingPrint.value = false), (isLoading.value = false)))
}

function onPrintPdf() {
  const iframe = document.createElement('iframe')
  iframe.src = pdfSource.value
  iframe.style.display = 'none'
  document.body.appendChild(iframe)

  // Use onload to make pdf preview work on firefox
  iframe.onload = () => {
    iframe.contentWindow.focus()
    iframe.contentWindow.print()
  }
}

function onDownloadPdf() {
  saveAs(pdfSource.value, pdfFilename.value)
}

function onToggle() {
  emit('toggle')
}

function resetForm() {
  submitted.value = false
  form.mode = 'triwulan'
  form.pabrik = null
  form.triwulan = null
  form.bulan = null
  form.tahun = new Date().getFullYear()
}
</script>

<template>
  <fx-report-container
    title="LAPORAN E-REPORT TRIWULAN"
    :loading="isLoadingExport"
    @toggle="onToggle"
  >
    <template #content>
      <form>
        <div class="formgrid grid">
          <div class="field col-12">
            <div class="font-bold">Filter</div>
          </div>
          <div class="field col-12 md:col-3">
            <AutoComplete
              v-model="form.pabrik"
              :min-length="2"
              :suggestions="list_pabrik"
              field="nama"
              class="w-full"
              input-class="w-full"
              placeholder="Pabrik"
              autofocus
              @complete="searchPabrik($event)"
            >
              <template #item="slotProps">
                <div>
                  <strong
                    >{{ slotProps.item.kode }} -
                    {{ slotProps.item.nama }}</strong
                  >
                </div>
              </template>
            </AutoComplete>
          </div>

          <div class="field col-12 md:col-2">
            <SelectButton
              v-model="form.mode"
              :options="list_mode"
              optionValue="value"
              optionLabel="name"
              class="w-full"
            />
          </div>

          <div v-if="form.mode === 'triwulan'" class="field col-12 md:col-2">
            <Dropdown
              v-model="v$.triwulan.$model"
              :options="list_triwulan"
              optionValue="id"
              optionLabel="nama"
              placeholder="Pilih Triwulan"
              class="w-full"
              :class="{
                'p-invalid': v$.triwulan.$invalid && submitted,
              }"
            />
            <small
              v-if="
                (v$.triwulan.$invalid && submitted) ||
                v$.triwulan.$pending.$response
              "
              class="p-error"
              >{{ v$.triwulan.required.$message }}</small
            >
          </div>

          <div v-else class="field col-12 md:col-2">
            <Dropdown
              v-model="v$.bulan.$model"
              :options="list_bulan"
              optionValue="id"
              optionLabel="nama"
              placeholder="Pilih Bulan"
              class="w-full"
              :class="{
                'p-invalid': v$.bulan.$invalid && submitted,
              }"
            />
            <small
              v-if="
                (v$.bulan.$invalid && submitted) || v$.bulan.$pending.$response
              "
              class="p-error"
              >{{ v$.bulan.required.$message }}</small
            >
          </div>

          <div class="field col-12 md:col-2">
            <Dropdown
              v-model="form.tahun"
              :options="list_tahun"
              placeholder="Pilih Tahun"
              class="w-full"
            />
          </div>

          <div class="field col-12 md:col-3">
            <Button
              :loading="isLoadingPrint"
              type="button"
              icon="pi pi-file-pdf"
              label="Cetak"
              class="mb-0 md:mb-2 xl:mb-0"
              @click="onPrint(!v$.$invalid)"
            />
            <Button
              :loading="isLoadingExport"
              type="button"
              icon="pi pi-file-excel"
              label="Export"
              class="p-button-success ml-2 md:ml-0 xl:ml-2"
              @click="onExport(!v$.$invalid)"
            />
            <Button
              type="reset"
              icon="pi pi-refresh"
              label="Reset"
              class="
                p-button-outlined
                ml-2
                md:ml-0
                xl:ml-2
                md:mt-2
                xl:mt-0
                btn-3xl-mt-2 btn-3xl-ml-0
              "
              @click="resetForm"
            />
          </div>
        </div>
      </form>
    </template>
  </fx-report-container>
  <Dialog
    :header="pdfTitle"
    v-model:visible="dialogViewer"
    :breakpoints="{ '960px': '75vw', '640px': '90vw' }"
    :style="{ width: '90vw' }"
    :modal="true"
  >
    <div>
      <embed
        :src="pdfSource"
        type="application/pdf"
        width="100%"
        height="600px"
      />
    </div>
    <template #footer>
      <Button
        type="button"
        label="Download"
        icon="pi pi-download"
        class="p-button-text mr-2"
        @click="onDownloadPdf"
      />
      <Button
        type="button"
        label="Print"
        icon="pi pi-print"
        @click="onPrintPdf"
      />
    </template>
  </Dialog>
</template>
